import type { SafeAreaInsets, TelegramThemeParams } from './telegramTypes.js';

const TELEGRAM_THEME_VARIABLES: Readonly<Record<keyof TelegramThemeParams, string>> = {
  bg_color: '--tg-bg-color',
  secondary_bg_color: '--tg-secondary-bg-color',
  text_color: '--tg-text-color',
  hint_color: '--tg-hint-color',
  link_color: '--tg-link-color',
  button_color: '--tg-button-color',
  button_text_color: '--tg-button-text-color',
};

const APPLICATION_THEME_VARIABLES = [
  '--color-bg-primary',
  '--color-bg-surface',
  '--color-bg-card',
  '--color-text-primary',
  '--color-text-secondary',
  '--color-text-muted',
  '--color-accent',
] as const;

function safeTelegramColor(value: unknown): string | null {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : null;
}

export function applyTelegramTheme(
  root: HTMLElement,
  themeParams: TelegramThemeParams,
  safeArea: SafeAreaInsets,
  contentSafeArea: SafeAreaInsets
): void {
  for (const [key, cssVariable] of Object.entries(TELEGRAM_THEME_VARIABLES)) {
    const color = safeTelegramColor(themeParams[key as keyof TelegramThemeParams]);
    if (color) root.style.setProperty(cssVariable, color);
    else root.style.removeProperty(cssVariable);
  }

  for (const cssVariable of APPLICATION_THEME_VARIABLES) root.style.removeProperty(cssVariable);

  const background = safeTelegramColor(themeParams.bg_color);
  const surface = safeTelegramColor(themeParams.secondary_bg_color);
  const text = safeTelegramColor(themeParams.text_color);
  const hint = safeTelegramColor(themeParams.hint_color);
  const accent =
    safeTelegramColor(themeParams.button_color) ?? safeTelegramColor(themeParams.link_color);

  if (background) root.style.setProperty('--color-bg-primary', background);
  if (surface) {
    root.style.setProperty('--color-bg-surface', surface);
    root.style.setProperty('--color-bg-card', surface);
  }
  if (text) root.style.setProperty('--color-text-primary', text);
  if (hint) {
    root.style.setProperty('--color-text-secondary', hint);
    root.style.setProperty('--color-text-muted', hint);
  }
  if (accent) root.style.setProperty('--color-accent', accent);

  root.style.setProperty('--tg-safe-area-top', `${safeArea.top}px`);
  root.style.setProperty('--tg-safe-area-right', `${safeArea.right}px`);
  root.style.setProperty('--tg-safe-area-bottom', `${safeArea.bottom}px`);
  root.style.setProperty('--tg-safe-area-left', `${safeArea.left}px`);
  root.style.setProperty('--tg-content-safe-area-top', `${contentSafeArea.top}px`);
  root.style.setProperty('--tg-content-safe-area-bottom', `${contentSafeArea.bottom}px`);
}
